import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredLevel?: 'admin' | 'socio' | 'master' | 'reseller' | 'subreseller';
  requiredPermission?: string;
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredLevel,
  requiredPermission,
  fallbackPath = '/login'
}) => {
  const { isAuthenticated, getUserLevel, hasPermission, user } = useAuth();
  const location = useLocation();

  // Verificar se está autenticado
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Verificar nível hierárquico se especificado
  if (requiredLevel) {
    const userLevel = getUserLevel();
    const memberGroupId = user?.member_group_id;

    // Admin sempre tem acesso
    if (memberGroupId === 1) {
      return <>{children}</>;
    }

    // Se a rota exige apenas revendedor ou sub-revendedor, permitir explicitamente por member_group_id
    // (evita bloqueio quando getUserLevel() falha ou hierarchy não está carregada)
    if (requiredLevel === 'subreseller' || requiredLevel === 'reseller') {
      if (memberGroupId === 4 || memberGroupId === 6) {
        return <>{children}</>;
      }
    }

    // Sócio (2) e Master (3) têm acesso a tudo exceto rotas admin-only
    if (memberGroupId === 2 || memberGroupId === 3) {
      if (requiredLevel !== 'admin') {
        return <>{children}</>;
      }
    }

    const levelHierarchy = {
      'admin': 5,
      'socio': 4,
      'master': 3,
      'reseller': 2,
      'subreseller': 1,
      'user': 0
    };

    const userLevelValue = levelHierarchy[userLevel as keyof typeof levelHierarchy] ?? (memberGroupId === 4 ? 2 : memberGroupId === 6 ? 1 : 0);
    const requiredLevelValue = levelHierarchy[requiredLevel];

    if (userLevelValue < requiredLevelValue) {
      console.warn('⚠️ [ProtectedRoute] Acesso negado - nível insuficiente:', {
        requiredLevel,
        requiredLevelValue,
        userLevel,
        userLevelValue,
        path: location.pathname,
        member_group_id: memberGroupId
      });
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Verificar permissão específica se especificada
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
